import React, { useEffect, useRef, useState, useCallback } from "react";
import { Flex, Colors, PADDING, GoalStyles } from "styles/global-style";
import RightSidebar from "components/RightSidebar";
import Input from "components/Input";
import Textarea from "components/Textarea";
import Button from "components/Button";
import Dropdown from "components/Dropdown";
import Checkbox from "components/Checkbox";
import ColorButton from "components/Timeline/ColorButton";
import xmark from "imgs/xmark.png";

export default function GoalPopup({ showEditGoal, editingGoal, isNewGoal, deleteGoal, submitGoal, closeGoal, disciplines = [], peopleAllFormatted = [] }) {
  const newTitle = useRef();
  const newBody = useRef();

  const [title, setTitle] = useState(editingGoal?.name || "");
  const [body, setBody] = useState(editingGoal?.body || "");
  const [isPrivateGoal, setIsPrivateGoal] = useState(Boolean(editingGoal?.private));

  const saveTimer = useRef(null);
  const lastSaved = useRef({ name: null, body: null });

  // Calculate available days per discipline - simply count green dots (working days) towards the goal
  const calculateAvailableDays = useCallback(() => {
    if (!editingGoal?.date || !peopleAllFormatted || peopleAllFormatted.length === 0) return {};

    const goalDate = new Date(editingGoal.date);
    goalDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // If goal is in the past, return empty
    if (goalDate < today) return {};

    const disciplineDays = {};

    // Count green dots (working days) for each discipline
    peopleAllFormatted.forEach((peep) => {
      const disciplineId = peep.person.discipline_id || null;

      // Count working days (green dots) between today and goal date
      const workingDays = peep.days.filter((day) => {
        // Must be active (person is in project on this day)
        if (!day.isActive) return false;
        
        // Must be a working day (green dot = style === "working")
        if (day.style !== "working") return false;
        
        // Must be between today and goal date (inclusive)
        // Always create a new Date object to avoid mutating the original
        const dayDate = new Date(day.day);
        dayDate.setHours(0, 0, 0, 0);
        if (dayDate < today || dayDate > goalDate) return false;
        
        return true;
      });

      // Add to discipline total
      if (!disciplineDays[disciplineId]) {
        disciplineDays[disciplineId] = 0;
      }
      disciplineDays[disciplineId] += workingDays.length;
    });

    return disciplineDays;
  }, [editingGoal?.date, peopleAllFormatted]);

  const availableDaysByDiscipline = calculateAvailableDays();

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  const flushSave = useCallback(
    (force) => {
      if (!editingGoal?.id) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = null;

      const trimmedName = (title || "").trim();
      const trimmedBody = (body || "").trim();

      const nameChanged = force || lastSaved.current.name !== trimmedName;
      const bodyChanged = force || lastSaved.current.body !== trimmedBody;

      if (!nameChanged && !bodyChanged) return;

      lastSaved.current = { name: trimmedName, body: trimmedBody };
      // keepOpen=true so this doesn't close the sidebar; we explicitly close via closeGoal()
      submitGoal({ id: editingGoal.id, name: trimmedName, body: trimmedBody }, true);
    },
    [editingGoal?.id, submitGoal, title, body]
  );

  const scheduleSave = useCallback(() => {
    if (!editingGoal?.id) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flushSave(false), 2000);
  }, [editingGoal?.id, flushSave]);

  const handleClose = useCallback(() => {
    const trimmedTitle = (title || "").trim();
    const trimmedBody = (body || "").trim();
    if (isNewGoal && !trimmedTitle && !trimmedBody) {
      closeGoal({ deleteNew: true });
    } else {
      flushSave(true);
      closeGoal();
    }
  }, [flushSave, closeGoal, isNewGoal, title, body]);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  useEffect(() => {
    if (!showEditGoal) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = null;
      return;
    }
    // Sync draft state on open / switching goals
    setTitle(editingGoal?.name || "");
    setBody(editingGoal?.body || "");
    setIsPrivateGoal(Boolean(editingGoal?.private));
    lastSaved.current = { name: (editingGoal?.name || "").trim(), body: (editingGoal?.body || "").trim() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditGoal, editingGoal?.id]); // intentionally key off id to avoid clobbering while typing

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  return (
    showEditGoal && (
      <RightSidebar closeCb={handleClose}>
        <div className="right-sidebar-header">
          <Input
            ref={newTitle}
            placeholder="Goal title…"
            onChange={(e) => {
              setTitle(e.target.value);
              scheduleSave();
            }}
            autoFocus={Boolean(showEditGoal && (!editingGoal?.name || !editingGoal.name.trim()))}
            left
            noborder
            value={title}
            maxLength={120}
            customStyle={{
              background: "transparent",
              padding: 0,
              margin: 0,
              boxShadow: "none",
              fontFamily: "inherit",
              fontSize: 22,
              fontWeight: 800,
              height: "auto",
              color: "#111",
            }}
          />

          <img
            alt="close"
            src={xmark}
            style={{ height: 18, width: 18, cursor: "pointer", opacity: 0.65 }}
            onClick={() => handleClose()}
          />
        </div>

        <div className="right-sidebar-body" style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", flexDirection: "column", rowGap: PADDING }}>
            <div style={{ marginLeft: PADDING / 2 }}>
              <Checkbox
                text={
                  <span>
                    Private goal <span style={{ fontWeight: 400 }}>(only visible to you)</span>
                  </span>
                }
                checked={isPrivateGoal}
                onChangeCb={(e) => {
                  const checked = e.target.checked;
                  setIsPrivateGoal(checked);
                  editingGoal.private = checked;
                  submitGoal({ private: checked, id: editingGoal.id }, true);
                }}
              />
            </div>

            <Flex gap={PADDING / 2} style={{ marginLeft: PADDING / 2, flexWrap: "wrap" }}>
              {Object.keys(Colors).map((color, i) => {
                if (["RED"].includes(color) || !showEditGoal) return <div key={"color" + i} />;
                const userColor = editingGoal?.color;
                const currColor = userColor ? userColor : GoalStyles[editingGoal.type].color;
                return (
                  <ColorButton
                    key={"color" + i}
                    color={Colors[color]}
                    active={Colors[color] === currColor}
                    clickCb={(color) => {
                      editingGoal.color = color;
                      submitGoal({ color: color, id: editingGoal.id }, true);
                    }}
                  />
                );
              })}

              <Dropdown
                items={Object.keys(GoalStyles).filter((g) => !["today", "massive", "unplanned"].includes(g))}
                defaultValue={editingGoal?.type}
                changeCb={(e) => {
                  submitGoal({ type: e.target.value, id: editingGoal.id }, true);
                }}
              />
            </Flex>

            <div style={{ marginLeft: PADDING / 2 }}>
              <Textarea
                ref={newBody}
                placeholder="Description of the goal"
                onChange={(e) => {
                  setBody(e.target.value);
                  scheduleSave();
                }}
                left
                value={body}
                customStyle={{
                  color: "#111",
                  border: "none",
                  fontFamily: "inherit",
                  fontSize: 14,
                  fontWeight: "normal",
                  background: "transparent",
                  textAlign: "left",
                  boxShadow: "none",
                  outline: "none",
                  padding: 0,
                  margin: 0,
                  resize: "none",
                  minHeight: 28,
                }}
                className="modal-notes-dark-placeholder"
              />
            </div>

            {/* Available days per discipline */}
            {editingGoal?.date && disciplines.length > 0 && Object.keys(availableDaysByDiscipline).length > 0 && (
              <div style={{ marginTop: PADDING / 2 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#999", marginBottom: PADDING / 2, marginLeft: PADDING / 2 }}>
                  Available days per discipline
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginLeft: PADDING / 2 }}>
                  {disciplines.map((discipline) => {
                    const days = availableDaysByDiscipline[discipline.id] || 0;
                    if (days === 0 && !availableDaysByDiscipline.hasOwnProperty(discipline.id)) return null;
                    return (
                      <div key={discipline.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 2,
                            backgroundColor: discipline.color || "#999",
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ fontSize: 14, color: "#111" }}>{discipline.name}</span>
                        <span style={{ fontSize: 14, color: "#666", marginLeft: "auto" }}>{days} days</span>
                      </div>
                    );
                  })}
                  {/* Show "No discipline" if there are people without discipline */}
                  {availableDaysByDiscipline.hasOwnProperty("null") && availableDaysByDiscipline["null"] > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          backgroundColor: "#999",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: 14, color: "#111" }}>No discipline</span>
                      <span style={{ fontSize: 14, color: "#666", marginLeft: "auto" }}>{availableDaysByDiscipline["null"]} days</span>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
          {editingGoal?.id && (
            <div style={{ marginTop: "auto", paddingTop: PADDING }}>
              <Button text="Delete goal" callback={() => deleteGoal(editingGoal.id)} color="red" />
            </div>
          )}
        </div>
      </RightSidebar>
    )
  );
}
